import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { LoggerService } from '../../../../core/services/logger.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, combineLatest, take, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Dependency,
  CreateDependencyRequest,
  UpdateDependencyRequest,
  DependencyFormData
} from '../../models/dependency.interface';
import { DEPENDENCY_LABELS } from './dependency.labels';
import * as DependencyActions from '../../store/dependency.actions';
import * as DependencySelectors from '../../store/dependency.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TrackByUtils } from '../../../../shared/utils/trackby.utils';
// No se usa el servicio para validación de código; se valida contra el store

@Component({
  selector: 'app-dependency',
  templateUrl: './dependency.component.html',
  styleUrls: ['./dependency.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    MatExpansionModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatPaginatorModule
  ]
})
export class DependencyComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = DEPENDENCY_LABELS;

  // Observables del store
  dependencies$ = this.store.select(DependencySelectors.selectAllDependencies);

  // Observable para resultados de búsqueda (no usado en filtering local por columnas)
  searchResults$ = this.store.select(DependencySelectors.selectDependencySearchResults);

  // Filtros por columnas
  columnFilters: { code: string; status: string; parent: string } = {
    code: '',
    status: '',
    parent: ''
  };

  private readonly filtersSubject = new BehaviorSubject<number>(0);
  filters$ = this.filtersSubject.asObservable();

  // Data source para la tabla con paginación
  dataSource = new MatTableDataSource<Dependency>([]);

  // Subject para manejar los datos de la tabla
  private tableDataSubject = new BehaviorSubject<Dependency[]>([]);
  tableData$ = this.tableDataSubject.asObservable();

  // Observable combinado para mostrar dependencias aplicando búsqueda y filtros por columna
  displayDependencies$ = combineLatest([this.dependencies$, this.filters$]).pipe(
    map(([dependencies]) => {
      let result = dependencies;

      // Búsqueda general
      const globalQuery = (this.searchQuery || '').toLowerCase().trim();
      if (globalQuery) {
        result = result.filter(
          d =>
            d.detalle.toLowerCase().includes(globalQuery) ||
            d.codigo.toLowerCase().includes(globalQuery)
        );
      }

      // Filtros por columna
      const { code, status, parent } = this.columnFilters;
      if (code && code.trim()) {
        const q = code.toLowerCase().trim();
        result = result.filter(d => d.codigo.toLowerCase().includes(q));
      }
      if (status && status.trim()) {
        const q = status.toLowerCase().trim();
        result = result.filter(d => (d.isDisponible ? 'activo' : 'inactivo').includes(q));
      }
      if (parent && parent.trim()) {
        const q = parent.toLowerCase().trim();
        result = result.filter(d => {
          if (!d.padreId) return 'sin padre'.includes(q) || q === '-' || q === 'null';
          const p = dependencies.find(x => x.dependenciaId === d.padreId);
          return p
            ? p.detalle.toLowerCase().includes(q) || (p.codigo || '').toLowerCase().includes(q)
            : false;
        });
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Observable adicional para debuggear el estado completo
  dependencyState$ = this.store.select(DependencySelectors.selectDependencyState);
  parentDependencies$ = this.store.select(DependencySelectors.selectParentDependencies);
  selectedDependency$ = this.store.select(DependencySelectors.selectSelectedDependency);
  dependencyDetailList$ = this.store.select(DependencySelectors.selectAllDependencies); // Para simular detalles
  loading$ = this.store.select(DependencySelectors.selectDependencyLoading);
  creating$ = this.store.select(DependencySelectors.selectDependencyCreating);
  updating$ = this.store.select(DependencySelectors.selectDependencyUpdating);
  toggling$ = this.store.select(DependencySelectors.selectDependencyToggling);
  searching$ = this.store.select(DependencySelectors.selectDependencySearching);
  error$ = this.store.select(DependencySelectors.selectDependencyError);

  // Formulario
  dependencyForm!: FormGroup;
  isEditing = false;
  editingDependency: Dependency | null = null;
  searchQuery = '';
  showSearchPanel = false;
  isCreatingDetail = false;
  dependencyDetailInEdit = false;
  isParentDetail = false;
  detailBeingViewed: Dependency | null = null;

  // Columnas de la tabla
  tableColumns: string[] = ['id', 'detail', 'code', 'status', 'parent', 'actions'];

  // TrackBy functions
  trackByDependencyId = TrackByUtils.trackByProperty<Dependency>('dependenciaId');

  @ViewChild('createDependencyModal') createDependencyModal!: TemplateRef<unknown>;
  @ViewChild('paginator') paginator!: MatPaginator;

  ngOnInit(): void {
    this.initializeForm();
    this.setupPagination();
    this.loadDependencies();
    this.loadParentDependencies();
    this.filtersSubject.next(0);

    // Suscripción directa para debuggear cambios en dependencias
    this.dependencies$
      .pipe(
        take(10) // Tomar los primeros 10 cambios
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    // Suscripción para debuggear el estado completo
    this.dependencyState$.pipe(take(10)).subscribe(() => {
      // Estado completo del store
    });

    // Eliminamos recargas automáticas; el store se actualiza desde efectos
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura la paginación
   */
  private setupPagination(): void {
    // Suscribirse a los cambios en displayDependencies$ para actualizar el data source
    this.displayDependencies$.subscribe(dependencies => {
      this.logger.debug('Updating data source with dependencies', { count: dependencies.length });

      // Actualizar el data source existente
      this.dataSource.data = dependencies;

      // Asegurar que el paginador esté conectado
      if (this.paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this.paginator;
      }

      // Actualizar el subject para mantener la sincronización
      this.tableDataSubject.next(dependencies);

      // Forzar la detección de cambios
      this.cdr.detectChanges();

      this.logger.debug('Data source updated', {
        dataLength: this.dataSource.data.length,
        paginatorConnected: !!this.dataSource.paginator,
        paginatorLength: this.paginator?.length
      });

      // Verificar el estado del paginador después de actualizar los datos
      setTimeout(() => this.checkPaginatorState(), 100);
    });
  }

  /**
   * Se ejecuta después de que se inicializa la vista
   */
  ngAfterViewInit(): void {
    // Conectar el paginador al data source inmediatamente
    this.dataSource.paginator = this.paginator;

    // Asegurar que el paginador esté configurado correctamente
    if (this.paginator) {
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Forzar la detección de cambios para asegurar que el paginador se conecte
    this.cdr.detectChanges();

    // Log para debug
    this.logger.debug('ngAfterViewInit - Paginator connected', {
      paginatorExists: !!this.paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataLength: this.dataSource.data.length,
      pageSize: this.paginator?.pageSize,
      pageSizeOptions: this.paginator?.pageSizeOptions
    });
  }

  /**
   * Maneja los cambios de página
   */
  onPageChange(): void {
    // El paginador se maneja automáticamente por el MatTableDataSource
    // Este método puede usarse para logging o acciones adicionales
    this.logger.debug('Page changed', {
      pageIndex: this.paginator?.pageIndex,
      pageSize: this.paginator?.pageSize,
      length: this.paginator?.length,
      dataSourceLength: this.dataSource.data.length,
      filteredDataLength: this.dataSource.filteredData.length
    });
  }

  /**
   * Verifica el estado del paginador
   */
  private checkPaginatorState(): void {
    this.logger.debug('Paginator state check', {
      paginatorExists: !!this.paginator,
      dataSourcePaginator: !!this.dataSource.paginator,
      dataSourceDataLength: this.dataSource.data.length,
      paginatorLength: this.paginator?.length,
      paginatorPageSize: this.paginator?.pageSize,
      paginatorPageIndex: this.paginator?.pageIndex
    });

    // Si el paginador no está conectado, intentar reconectarlo
    if (this.paginator && !this.dataSource.paginator) {
      this.logger.debug('Reconnecting paginator to data source');
      this.dataSource.paginator = this.paginator;
      this.cdr.detectChanges();
    }
  }

  /**
   * Aplica filtros y resetea la paginación
   */
  applyFilters(): void {
    this.filtersSubject.next(Date.now());
    // Reset pagination to first page when filters change
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  /**
   * Limpia los filtros y resetea la paginación
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.columnFilters = { code: '', status: '', parent: '' };
    this.filtersSubject.next(Date.now());
    // Reset pagination to first page when filters change
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  /**
   * Inicializa el formulario
   */
  private initializeForm(): void {
    this.dependencyForm = this.formBuilder.group({
      detalle: ['', [Validators.required, Validators.minLength(3)]],
      codigo: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(9)]],
      isDisponible: [true],
      padreId: [null]
    });
  }

  /** Valida unicidad del código al perder el foco */
  async onCodeBlur(): Promise<void> {
    const codigoControl = this.dependencyForm.get('codigo');
    const code = (codigoControl?.value ?? '').toString().trim();
    if (!codigoControl) return;
    if (!code) {
      const rest = { ...(codigoControl.errors || {}) };
      delete (rest as Record<string, unknown>)['codeExists'];
      if (Object.keys(rest).length > 0) {
        codigoControl.setErrors(rest);
      } else {
        codigoControl.setErrors(null);
      }
      return;
    }
    const dependencies = await firstValueFrom(this.dependencies$);
    const excludeId =
      this.isEditing && this.editingDependency ? this.editingDependency.dependenciaId : undefined;
    const exists = (dependencies || []).some(
      d => d.codigo?.toLowerCase() === code.toLowerCase() && d.dependenciaId !== excludeId
    );
    if (exists) {
      codigoControl.setErrors({ ...(codigoControl.errors || {}), codeExists: true });
    } else if (codigoControl.hasError('codeExists')) {
      const rest = { ...(codigoControl.errors || {}) };
      delete (rest as Record<string, unknown>)['codeExists'];
      if (Object.keys(rest).length > 0) {
        codigoControl.setErrors(rest);
      } else {
        codigoControl.setErrors(null);
      }
    }
  }

  /**
   * Carga las dependencias
   */
  loadDependencies(): void {
    this.store.dispatch(DependencyActions.loadDependencies());
  }

  /**
   * Carga las dependencias padre para el dropdown
   */
  loadParentDependencies(): void {
    this.store.dispatch(DependencyActions.loadParentDependencies());
  }

  /**
   * Abre el modal para crear/editar dependencia
   */
  openModal(dependency?: Dependency): void {
    this.isEditing = !!dependency;
    this.editingDependency = dependency || null;

    if (dependency) {
      this.dependencyForm.patchValue({
        detalle: dependency.detalle,
        codigo: dependency.codigo,
        isDisponible: dependency.isDisponible,
        padreId: dependency.padreId
      });
    } else {
      this.dependencyForm.reset({
        isDisponible: true,
        padreId: null
      });
    }

    const dialogRef = this.dialog.open(this.createDependencyModal, {
      width: '600px',
      disableClose: true
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.resetForm();
      });
  }

  /**
   * Guarda la dependencia (crear o actualizar)
   */
  async saveDependency(): Promise<void> {
    if (this.dependencyForm.valid) {
      const formData: DependencyFormData = this.dependencyForm.value;

      // R3: validación de regla de padre
      const validParent = await this.validateParentRule(formData.padreId);
      if (!validParent) {
        return;
      }

      // R1: validación final de unicidad
      const excludeId =
        this.isEditing && this.editingDependency ? this.editingDependency.dependenciaId : undefined;
      const dependencies = await firstValueFrom(this.dependencies$);
      const exists = (dependencies || []).some(
        d =>
          d.codigo?.toLowerCase() === formData.codigo.trim().toLowerCase() &&
          d.dependenciaId !== (excludeId ?? -1)
      );
      if (exists) {
        this.notificationService.showErrorModal(
          'Código interno repetido',
          this.isEditing ? 'No se pudo editar la Dependencia' : 'No se pudo crear la Dependencia'
        );
        this.dependencyForm.get('codigo')?.setErrors({ codeExists: true });
        return;
      }

      if (this.isEditing && this.editingDependency) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar esta dependencia?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          const updateRequest: UpdateDependencyRequest = {
            dependenciaId: this.editingDependency.dependenciaId,
            ...formData
          };

          this.store.dispatch(
            DependencyActions.updateDependency({
              id: this.editingDependency.dependenciaId,
              dependency: updateRequest
            })
          );

          // Cerrar modal después de actualizar
          this.closeModal();
          this.cdr.detectChanges();
        }
      } else {
        // Para crear, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear esta dependencia?',
          'Confirmar Creación'
        );

        if (confirmed) {
          const createRequest: CreateDependencyRequest = formData;

          this.store.dispatch(DependencyActions.createDependency({ dependency: createRequest }));

          // Cerrar modal después de crear
          this.closeModal();
          this.cdr.detectChanges();
        }
      }
    } else {
      this.markFormGroupTouched();
    }
  }

  /**
   * Activa o desactiva una dependencia
   */
  async toggleDependencyStatus(dependency: Dependency): Promise<void> {
    const newStatus = !dependency.isDisponible;
    const action = newStatus ? 'activar' : 'desactivar';

    const confirmed = await this.notificationService.showConfirmation(
      `¿Está seguro de que desea ${action} esta dependencia?`,
      'Confirmar cambio de estado'
    );

    if (confirmed) {
      this.store.dispatch(
        DependencyActions.toggleDependencyStatus({
          id: dependency.dependenciaId,
          isActive: newStatus
        })
      );

      // El store se actualiza con la respuesta del efecto; no recargamos manualmente
    }
  }

  /**
   * Busca dependencias
   */
  searchDependencies(query: string): void {
    this.searchQuery = query;
    this.applyFilters();
  }

  /**
   * Alterna el panel de búsqueda
   */
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
    if (!this.showSearchPanel) {
      this.resetFilters();
    }
  }

  /**
   * Selecciona una dependencia
   */
  selectDependency(dependency: Dependency): void {
    this.store.dispatch(DependencyActions.selectDependency({ dependency }));
  }

  /**
   * Elimina una dependencia con confirmación
   */
  async deleteDependency(dependency: Dependency): Promise<void> {
    const confirmed = await this.notificationService.showConfirmation(
      this.labels.confirmDelete,
      'Confirmar eliminación'
    );

    if (confirmed) {
      this.store.dispatch(DependencyActions.deleteDependency({ id: dependency.dependenciaId }));

      // El store elimina el elemento en deleteSuccess; no recargamos
    }
  }

  /**
   * Obtiene el nombre de la dependencia padre
   */
  getParentName(parentId: number | null, dependencies: Dependency[]): string {
    if (!parentId) return this.labels.notSelected;
    const parent = dependencies.find(d => d.dependenciaId === parentId);
    return parent ? parent.detalle : 'N/A';
  }

  /**
   * Obtiene el estado formateado
   */
  getStatusText(isDisponible: boolean): string {
    return isDisponible ? this.labels.active : this.labels.inactive;
  }

  /**
   * Obtiene la clase CSS para el estado
   */
  getStatusClass(isDisponible: boolean): string {
    return isDisponible ? 'status-active' : 'status-inactive';
  }

  /**
   * Marca todos los campos del formulario como touched
   */
  private markFormGroupTouched(): void {
    Object.keys(this.dependencyForm.controls).forEach(key => {
      const control = this.dependencyForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Resetea el formulario
   */
  private resetForm(): void {
    this.dependencyForm.reset({
      isDisponible: true,
      padreId: null
    });
    this.isEditing = false;
    this.editingDependency = null;
  }

  /**
   * R3: Valida la relación con el padre
   */
  private async validateParentRule(parentId: number | null): Promise<boolean> {
    if (parentId === null || parentId === undefined) {
      return true;
    }
    if (
      this.isEditing &&
      this.editingDependency &&
      parentId === this.editingDependency.dependenciaId
    ) {
      this.notificationService.showErrorModal(
        'Dependencia Padre inválida',
        'La dependencia no puede ser su propio padre'
      );
      return false;
    }
    const parents = await firstValueFrom(this.parentDependencies$);
    const isRootParent = (parents || []).some(
      p => p.dependenciaId === parentId && (p.padreId === null || p.padreId === undefined)
    );
    if (!isRootParent) {
      this.notificationService.showErrorModal(
        'Dependencia Padre inválida',
        'Debe seleccionar una dependencia de nivel raíz'
      );
      return false;
    }
    return true;
  }

  /**
   * Obtiene el mensaje de error para un campo
   */
  getErrorMessage(controlName: string): string {
    const control = this.dependencyForm.get(controlName);

    if (control?.hasError('required')) {
      switch (controlName) {
        case 'detalle':
          return this.labels.detailRequired;
        case 'codigo':
          return this.labels.codeRequired;
        default:
          return 'Este campo es obligatorio';
      }
    }

    if (control?.hasError('minlength')) {
      switch (controlName) {
        case 'detalle':
          return this.labels.detailMinLength;
        case 'codigo':
          return this.labels.codeMinLength;
        default:
          return 'Longitud mínima no alcanzada';
      }
    }

    if (control?.hasError('maxlength')) {
      switch (controlName) {
        case 'codigo':
          return this.labels.codeMaxLength;
        default:
          return 'Longitud máxima excedida';
      }
    }

    if (controlName === 'codigo' && control?.hasError('codeExists')) {
      return 'El código ya existe';
    }

    return '';
  }

  /**
   * Verifica si un campo tiene error
   */
  hasError(controlName: string): boolean {
    const control = this.dependencyForm.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  /**
   * Maneja el cambio de selección de dependencia
   */
  onDependencySelectionChange(dependency: Dependency | null): void {
    if (dependency) {
      this.store.dispatch(DependencyActions.selectDependency({ dependency }));
    }
  }

  /** Actualiza filtros por columna */
  onColumnFilterChange(field: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[field] = value;
    this.applyFilters();
  }

  /** Limpia búsqueda y filtros */
  resetFilters(): void {
    this.clearFilters();
  }

  /**
   * Muestra el detalle de una dependencia
   */
  viewDetail(dependency: Dependency, isParent: boolean): void {
    this.detailBeingViewed = dependency;
    this.isParentDetail = isParent;

    this.dialog.open(this.detailModal, {
      width: '500px',
      disableClose: false
    });
  }

  /**
   * Cierra el modal
   */
  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  @ViewChild('detailModal') detailModal!: TemplateRef<unknown>;
}
